import { memo, useState, useCallback, useRef } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Plus, Database, Pencil, Layers, Trash2, GripVertical, ArrowDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import { useUIStore } from '@/stores/ui-store';
import type { ActionNodeData, MMMCard } from '@/types/field-mapping-flow';

function makeRandomId(length = 7): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function ActionNodeComponent({ data }: NodeProps) {
  const nodeData = data as unknown as ActionNodeData;
  const selectCard = useFlowBuilderStore((s) => s.selectCard);
  const selectBranch = useFlowBuilderStore((s) => s.selectBranch);
  const removeCard = useFlowBuilderStore((s) => s.removeCard);
  const addCard = useFlowBuilderStore((s) => s.addCard);
  const reorderCards = useFlowBuilderStore((s) => s.reorderCards);
  const setActiveSidebarSection = useFlowBuilderStore((s) => s.setActiveSidebarSection);
  const selectedBranchId = useFlowBuilderStore((s) => s.selectedBranchId);
  const isDark = useUIStore((s) => s.theme) === 'dark';

  const isSelected = selectedBranchId === nodeData.docId;
  const cards = nodeData.cards || [];
  const isMultiModule = nodeData.isMultiModule;
  const { ignoreNewCustomers, ignoreExistingCustomers } = nodeData;
  const [isDragOver, setIsDragOver] = useState(false);

  // Internal card reorder state
  const [dragCardIndex, setDragCardIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const dragCounterRef = useRef(0);

  const handleAddCard = () => {
    if (nodeData.docId) {
      selectBranch(nodeData.docId);
      setActiveSidebarSection('modules');
    }
  };

  const handleCardClick = (cardId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (nodeData.docId) {
      selectBranch(nodeData.docId);
      selectCard(cardId);
    }
  };

  const handleRemoveCard = (cardId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (nodeData.docId) {
      removeCard(nodeData.docId, cardId);
    }
  };

  // ── Module drop from sidebar ──
  const handleNodeDragOver = useCallback((e: React.DragEvent) => {
    // Only handle sidebar module drops (not internal card reorder)
    if (e.dataTransfer.types.includes('application/flow-module')) {
      // Non-MMM apps: only one module allowed per branch
      if (!isMultiModule && cards.length > 0) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setIsDragOver(true);
    }
  }, [isMultiModule, cards.length]);

  const handleNodeDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleNodeDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const moduleDataStr = e.dataTransfer.getData('application/flow-module');
    if (!moduleDataStr || !nodeData.docId) return;

    // Non-MMM apps: only one module allowed per branch
    if (!isMultiModule && cards.length > 0) return;

    try {
      const { moduleValue, moduleLabel } = JSON.parse(moduleDataStr) as {
        moduleValue: string;
        moduleLabel: string;
      };

      const store = useFlowBuilderStore.getState();
      const mapping = store.fieldMappings[nodeData.docId];
      if (mapping?.fields && mapping.isMMM) {
        const existingCards = Object.values(mapping.fields).filter(
          (val): val is MMMCard =>
            typeof val === 'object' && val !== null && 'cardId' in val
        );
        if (existingCards.some((c) => c.saasFieldModuleName === moduleValue)) return;
      }

      const cardId = makeRandomId(7);
      const newCard: MMMCard = {
        cardId,
        saasFieldModuleName: moduleValue,
        displayLabel: moduleLabel,
        orderOf: cards.length,
        fields: {},
        fields_type: {},
        ct: Date.now(),
      };

      selectBranch(nodeData.docId);
      addCard(nodeData.docId, newCard);
    } catch {
      // ignore
    }
  }, [nodeData.docId, cards.length, selectBranch, addCard, isMultiModule]);

  // ── Internal card reorder drag handlers ──
  const handleCardDragStart = (e: React.DragEvent, index: number) => {
    e.stopPropagation();
    e.dataTransfer.setData('application/card-reorder', String(index));
    e.dataTransfer.effectAllowed = 'move';
    setDragCardIndex(index);
  };

  const handleCardDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('application/card-reorder')) {
      e.dataTransfer.dropEffect = 'move';
      setDropTargetIndex(index);
    }
  };

  const handleCardDragLeave = (e: React.DragEvent) => {
    e.stopPropagation();
  };

  const handleCardDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    const fromIndexStr = e.dataTransfer.getData('application/card-reorder');
    if (fromIndexStr === '' || !nodeData.docId) return;

    const fromIndex = parseInt(fromIndexStr, 10);
    if (fromIndex !== toIndex) {
      reorderCards(nodeData.docId, fromIndex, toIndex);
    }

    setDragCardIndex(null);
    setDropTargetIndex(null);
  };

  const handleCardDragEnd = () => {
    setDragCardIndex(null);
    setDropTargetIndex(null);
  };

  // ── Dark-mode-aware colors ──
  const nodeBg = isDark
    ? 'linear-gradient(135deg, #042f2e 0%, #134e4a 100%)'
    : 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)';
  const nodeBorder = isDark
    ? `1px solid ${isDragOver ? '#14b8a6' : isSelected ? '#2dd4bf' : '#115e59'}`
    : `1px solid ${isDragOver ? '#14b8a6' : isSelected ? '#2dd4bf' : '#99f6e4'}`;
  const dragOverBg = isDark
    ? 'linear-gradient(135deg, #134e4a 0%, #115e59 100%)'
    : 'linear-gradient(135deg, #e6fffa 0%, #b2f5ea 100%)';
  const headerBorderColor = isDark ? '#115e59' : '#a7f3d0';
  const iconBg = isDark ? '#0f766e' : '#0d9488';
  const labelColor = isDark ? '#2dd4bf' : '#0d9488';
  const addBtnColor = isDark ? '#2dd4bf' : '#0d9488';
  const arrowColor = isDark ? '#115e59' : '#99f6e4';
  const handleDotBg = isDark ? '#042f2e' : 'white';
  const emptyBorderColor = isDark ? '#115e59' : '#99f6e4';
  const emptyBg = isDark ? 'rgba(20, 30, 30, 0.5)' : 'rgba(255,255,255,0.5)';
  const cardItemBg = isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255,255,255,0.7)';
  const cardItemBorder = isDark ? '#115e59' : '#d1fae5';
  const cardItemDropBorder = isDark ? '#2dd4bf' : '#14b8a6';

  return (
    <div
      className={`
        rounded-xl shadow-md transition-all min-w-[220px] max-w-[280px]
        ${isSelected ? 'ring-2 ring-teal-300/40' : 'hover:shadow-lg'}
        ${isDragOver ? 'ring-2 ring-teal-400/60' : ''}
      `}
      style={{
        background: isDragOver ? dragOverBg : nodeBg,
        border: nodeBorder,
      }}
      onDragOver={handleNodeDragOver}
      onDragLeave={handleNodeDragLeave}
      onDrop={handleNodeDrop}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!h-2.5 !w-2.5 !border-2"
        style={{ borderColor: '#14b8a6', background: handleDotBg }}
      />

      {/* Header */}
      <div className="flex items-center gap-2.5 px-3.5 py-2.5" style={{ borderBottom: `1px solid ${headerBorderColor}` }}>
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg shadow-sm" style={{ background: iconBg }}>
          <Layers className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: labelColor }}>
            Actions
          </p>
          {nodeData.name && (
            <p className="truncate text-[10px] text-muted-foreground">{nodeData.name}</p>
          )}
        </div>
      </div>

      {/* Cards list */}
      <div className="p-2 space-y-0">
        {cards.length === 0 ? (
          isMultiModule ? (
            <div
              className="rounded-lg px-3 py-3.5 text-center"
              style={{ border: `1px dashed ${emptyBorderColor}`, background: emptyBg }}
            >
              <p className="text-[11px] text-muted-foreground font-medium">No module cards</p>
              <p className="text-[10px] text-muted-foreground/70 mt-0.5">Drag from sidebar or click below</p>
            </div>
          ) : (
            <button
              className="nodrag flex w-full items-center gap-2.5 rounded-lg px-3 py-3 text-left transition-colors hover:opacity-80"
              style={{ border: `1px dashed ${emptyBorderColor}`, background: emptyBg }}
              onClick={(e) => {
                e.stopPropagation();
                if (nodeData.docId) {
                  selectBranch(nodeData.docId);
                  useFlowBuilderStore.getState().setFieldMappingPanelOpen(true);
                }
              }}
            >
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md" style={{ background: iconBg }}>
                <Plus className="h-3 w-3 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium" style={{ color: addBtnColor }}>Select Module</p>
                <p className="text-[10px] text-muted-foreground/70">Choose a CRM module for this branch</p>
                {ignoreNewCustomers && (
                  <p className="text-[9px] font-medium text-orange-600 dark:text-orange-400 whitespace-nowrap">Proceed only with existing records</p>
                )}
                {ignoreExistingCustomers && (
                  <p className="text-[9px] font-medium text-orange-600 dark:text-orange-400 whitespace-nowrap">Proceed only with new records</p>
                )}
              </div>
            </button>
          )
        ) : (
          cards.map((card: MMMCard, index: number) => {
            const createFieldCount = Object.keys(card.fields || {}).length;
            const updateFieldCount = Object.keys(card.fieldsForExisting?.fields || {}).length;
            const hasExisting = updateFieldCount > 0;
            const isDragging = dragCardIndex === index;
            const isDropTarget = dropTargetIndex === index && dragCardIndex !== null && dragCardIndex !== index;

            return (
              <div key={card.cardId}>
                {/* Connection arrow between cards */}
                {index > 0 && (
                  <div className="flex justify-center py-1">
                    <ArrowDown className="h-3 w-3" style={{ color: arrowColor }} />
                  </div>
                )}

                {/* Card item */}
                <div
                  draggable={isMultiModule}
                  onDragStart={isMultiModule ? (e) => handleCardDragStart(e, index) : undefined}
                  onDragOver={isMultiModule ? (e) => handleCardDragOver(e, index) : undefined}
                  onDragLeave={isMultiModule ? handleCardDragLeave : undefined}
                  onDrop={isMultiModule ? (e) => handleCardDrop(e, index) : undefined}
                  onDragEnd={isMultiModule ? handleCardDragEnd : undefined}
                  className={`
                    nodrag group flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-all
                    ${isDragging ? 'opacity-40' : ''}
                    ${isDropTarget ? 'ring-2 ring-teal-400 ring-offset-1' : ''}
                  `}
                  style={{
                    background: cardItemBg,
                    border: `1px solid ${isDropTarget ? cardItemDropBorder : cardItemBorder}`,
                  }}
                  onClick={(e) => handleCardClick(card.cardId, e)}
                >
                  {/* Drag handle — only for multi-module */}
                  {isMultiModule && (
                    <GripVertical
                      className="h-3 w-3 shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/50 group-hover:text-muted-foreground"
                    />
                  )}

                  {/* Step number + icon */}
                  <div className="relative flex h-6 w-6 shrink-0 items-center justify-center rounded-md" style={{ background: iconBg }}>
                    <Database className="h-3 w-3 text-white" />
                    {isMultiModule && (
                      <span
                        className="absolute -top-1.5 -right-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] font-bold text-white"
                        style={{ background: '#0f766e' }}
                      >
                        {index + 1}
                      </span>
                    )}
                  </div>

                  {/* Card info */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-gray-900 dark:text-foreground">
                      {card.displayLabel || card.saasFieldModuleName}
                    </p>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground">
                        {hasExisting
                          ? `${createFieldCount} create · ${updateFieldCount} update`
                          : `${createFieldCount} ${createFieldCount === 1 ? 'field' : 'fields'}`
                        }
                      </span>
                    </div>
                    {ignoreNewCustomers && (
                      <p className="text-[9px] font-medium text-orange-600 dark:text-orange-400 whitespace-nowrap">Proceed only with existing records</p>
                    )}
                    {ignoreExistingCustomers && (
                      <p className="text-[9px] font-medium text-orange-600 dark:text-orange-400 whitespace-nowrap">Proceed only with new records</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      onClick={(e) => handleCardClick(card.cardId, e)}
                      className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                      title="Edit"
                    >
                      <Pencil className="h-2.5 w-2.5" />
                    </button>
                    <button
                      onClick={(e) => handleRemoveCard(card.cardId, e)}
                      className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-red-500/10 hover:text-red-500"
                      title="Remove"
                    >
                      <Trash2 className="h-2.5 w-2.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* Add card button — only for multi-module apps */}
        {isMultiModule && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-1.5 w-full justify-start gap-1.5 text-xs"
            style={{ color: addBtnColor }}
            onClick={handleAddCard}
          >
            <Plus className="h-3 w-3" />
            Add Module Card
          </Button>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!h-2.5 !w-2.5 !border-2"
        style={{ borderColor: '#14b8a6', background: handleDotBg }}
      />
    </div>
  );
}

export const ActionNode = memo(ActionNodeComponent);
